import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Trash2, FileText, Image, File, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRef, useState } from 'react';

interface Attachment {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  created_at: string;
}

interface ClienteAnexosTabProps {
  customerId: string;
  companyId: string;
}

function getFileIcon(fileType: string | null) {
  if (!fileType) return File;
  if (fileType.startsWith('image/')) return Image;
  if (fileType.includes('pdf')) return FileText;
  return File;
}

export function ClienteAnexosTab({ customerId, companyId }: ClienteAnexosTabProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const { data: attachments = [], isLoading } = useQuery({
    queryKey: ['customer-attachments', customerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customer_attachments')
        .select('*')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Attachment[];
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Upload to storage
      const fileName = `${customerId}/${Date.now()}_${file.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('customer-attachments')
        .upload(fileName, file);

      if (uploadError) {
        // If bucket doesn't exist, save URL placeholder
        console.warn('Storage upload failed, saving metadata only');
      }

      const fileUrl = uploadData?.path 
        ? supabase.storage.from('customer-attachments').getPublicUrl(uploadData.path).data.publicUrl
        : `placeholder://${file.name}`;

      // Save metadata
      const { error: dbError } = await supabase
        .from('customer_attachments')
        .insert({
          customer_id: customerId,
          company_id: companyId,
          file_name: file.name,
          file_url: fileUrl,
          file_type: file.type,
          uploaded_by: user.id
        });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-attachments', customerId] });
      setUploading(false);
    },
    onError: () => {
      setUploading(false);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (attachment: Attachment) => {
      // Delete from storage if real file
      if (!attachment.file_url.startsWith('placeholder://')) {
        const path = attachment.file_url.split('/').slice(-2).join('/');
        await supabase.storage.from('customer-attachments').remove([path]);
      }

      // Delete metadata
      const { error } = await supabase
        .from('customer_attachments')
        .delete()
        .eq('id', attachment.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-attachments', customerId] });
    }
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div 
        className="border-2 border-dashed border-[hsl(var(--basic-200))] rounded-xl p-8 text-center hover:border-[hsl(var(--basic-400))] transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <input 
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
        />
        <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {uploading ? 'Enviando...' : 'Clique ou arraste arquivos para fazer upload'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          PDF, imagens, documentos (máx. 10MB)
        </p>
      </div>

      {/* Attachments List */}
      {isLoading ? (
        <div className="p-8 text-center text-muted-foreground">Carregando...</div>
      ) : attachments.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground italic bg-muted/30 rounded-lg">
          Nenhum anexo registrado.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {attachments.map((attachment) => {
            const Icon = getFileIcon(attachment.file_type);
            return (
              <div 
                key={attachment.id} 
                className="flex items-center gap-3 p-4 bg-card border rounded-xl hover:shadow-sm transition-shadow"
              >
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--basic-100))] flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-[hsl(var(--basic-600))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-[hsl(var(--basic-900))] truncate">
                    {attachment.file_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(attachment.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div className="flex gap-1">
                  {!attachment.file_url.startsWith('placeholder://') && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8"
                      asChild
                    >
                      <a href={attachment.file_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteMutation.mutate(attachment)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
